import { useState, useEffect, useCallback } from 'react';
import cbsSubscriptionsService from '~/src/services/api/cbsSubscriptionsService';
import useUser from '~/src/hooks/useUser';

type BusinessEntityAction = 'show' | 'edit';

interface FetchBusinessEntityOptions {
  action: BusinessEntityAction;
  onSuccess?: (url: string) => void;
  setLoadingFn?: (loading: boolean) => void;
  errorMessage?: {
    noResponse: string;
    fallback: string;
  };
  silentError?: boolean;
}

export const useBusinessEntity = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editLoading, setEditLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [viewIframeUrl, setViewIframeUrl] = useState<string | null>(null);
  const [editIframeUrl, setEditIframeUrl] = useState<string | null>(null);
  const { currentOrganization } = useUser();
  const BUSINESS_ENTITY_UPDATED_MESSAGE = 'businessEntityUpdated';

  const fetchBusinessEntity = useCallback(
    async ({
      action,
      onSuccess = () => {},
      setLoadingFn = () => {},
      errorMessage = { noResponse: '', fallback: '' },
      silentError = false,
    }: FetchBusinessEntityOptions) => {
      const setErrorIfNotSilent = (errorMsg: string | null) => {
        if (!silentError) {
          setError(errorMsg);
        }
      };

      if (!currentOrganization?.fprint) {
        const errorMsg = 'Organization information not available';
        setErrorIfNotSilent(errorMsg);
        setLoadingFn(false);
        return;
      }

      setLoadingFn(true);
      setErrorIfNotSilent(null);

      try {
        const response = await cbsSubscriptionsService.getTaxIdentifierUrl(
          currentOrganization.fprint,
          action,
          'light',
        );

        if (response?.business_entities_url) {
          onSuccess(response.business_entities_url);
        } else {
          setErrorIfNotSilent(errorMessage.noResponse);
        }
      } catch (err: any) {
        setErrorIfNotSilent(err?.message || errorMessage.fallback);
      } finally {
        setLoadingFn(false);
      }
    },
    [currentOrganization?.fprint],
  );

  useEffect(() => {
    if (currentOrganization?.fprint) {
      fetchBusinessEntity({
        action: 'show',
        onSuccess: setViewIframeUrl,
        setLoadingFn: setLoading,
        errorMessage: {
          noResponse: 'Unable to load business entity information',
          fallback:
            'Unable to load business entity information. Please try again later.',
        },
      });
    }
  }, [currentOrganization?.fprint, fetchBusinessEntity]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.message === BUSINESS_ENTITY_UPDATED_MESSAGE &&
        event.data?.firmName
      ) {
        currentOrganization?.setOrganizationName(event.data.firmName);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [currentOrganization]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.message === BUSINESS_ENTITY_UPDATED_MESSAGE &&
        event.data?.firmName
      ) {
        currentOrganization?.setOrganizationName(event.data.firmName);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [currentOrganization]);

  const handleEdit = useCallback(async () => {
    await fetchBusinessEntity({
      action: 'edit',
      onSuccess: (url) => {
        setEditIframeUrl(url);
        setShowModal(true);
      },
      setLoadingFn: setEditLoading,
      errorMessage: {
        noResponse: 'Unable to load business entity management page',
        fallback:
          'Unable to load business entity management. Please try again later.',
      },
    });
  }, [fetchBusinessEntity]);

  const handleClose = useCallback(async () => {
    setShowModal(false);

    await fetchBusinessEntity({
      action: 'show',
      onSuccess: setViewIframeUrl,
      setLoadingFn: () => {},
      silentError: true,
    });
  }, [fetchBusinessEntity]);

  return {
    error,
    loading,
    editLoading,
    showModal,
    viewIframeUrl,
    editIframeUrl,
    handleEdit,
    handleClose,
  };
};
